import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Shares the "open in new tab" URL for the currently embedded application.
 *
 * Only `EmbedApp` knows which environment link the user is actually viewing —
 * in non-prod that is the selected alpha/beta/localhost env, not the prod
 * `applicationLink`. The external-link button lives in the top nav
 * (`TopNavActions`), a separate subtree, so `EmbedApp` publishes the resolved
 * link here and the button reads it. `null` means "no embed active / nothing
 * resolved yet" — the button falls back to the app's default link.
 *
 * The published URL is the clean, sanitized BASE link (no `?embed=true` params):
 * the external tab opens the app standalone, not as an embed.
 */
interface EmbeddedAppExternalLinkContextValue {
  externalUrl: string | null;
  setExternalUrl: (url: string | null) => void;
}

const EmbeddedAppExternalLinkContext = createContext<EmbeddedAppExternalLinkContextValue>({
  externalUrl: null,
  setExternalUrl: () => {}
});

export const EmbeddedAppExternalLinkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [externalUrl, setExternalUrl] = useState<string | null>(null);

  // `setExternalUrl` (from useState) has a stable identity, so the value only
  // needs to change when the URL itself changes.
  const value = useMemo<EmbeddedAppExternalLinkContextValue>(() => ({ externalUrl, setExternalUrl }), [externalUrl]);

  return <EmbeddedAppExternalLinkContext.Provider value={value}>{children}</EmbeddedAppExternalLinkContext.Provider>;
};

export const useEmbeddedAppExternalLink = (): EmbeddedAppExternalLinkContextValue => useContext(EmbeddedAppExternalLinkContext);
